/**
 * Normalizes the `salesPlaybookUsage` block of the session-resume response
 * (`GET /api/conversations/sessions/{botId}/resume`) into the Redux restore
 * payload. This is the single place the API shape is mapped into the
 * `playbookName`-keyed frontend state; call sites must not read the raw
 * block. All reads are defensive: a missing/partial block returns null or
 * safe empty structures instead of throwing.
 */
import {
  SalesPlaybookInfo,
  SalesPlaybookRoleInfo,
  SalesPlaybookRestorePayload,
  SelectionTrackingData,
} from '../store/slices/salesPlaybookSlice';

interface SalesPlaybookUsageRestore {
  restorePayload: SalesPlaybookRestorePayload;
  /** Role whose data should be fetched to populate the panel. */
  defaultRole: string;
}

const createEmptyTrackingEntry = (): SelectionTrackingData => ({
  askedQuestions: [],
  questionNotes: {},
  checkedPitches: [],
  pitchNotes: {},
  checkedAdvanced: [],
  advancedNotes: {},
  legacyPitchTexts: {},
  legacyAdvancedTexts: {},
});

export function buildSalesPlaybookRestore(
  salesPlaybookUsage: any,
  availablePlaybooks: SalesPlaybookInfo[] | undefined
): SalesPlaybookUsageRestore | null {
  const selectedPlaybookName = salesPlaybookUsage?.playbookName || salesPlaybookUsage?.selectedPlaybookName;
  if (!selectedPlaybookName) {
    return null;
  }

  // Find the matching playbook to get available roles
  const matchingPlaybook = (availablePlaybooks || []).find(
    (playbook) => playbook?.playbookName === selectedPlaybookName
  );
  const availableRoles: SalesPlaybookRoleInfo[] = matchingPlaybook?.roles || [];

  // Determine default role (use saved role, or first available, or 'general')
  const defaultRole: string =
    salesPlaybookUsage?.selectedRole || availableRoles[0]?.name || 'general';
  const defaultRoleDisplayName: string =
    salesPlaybookUsage?.selectedRoleDisplayName ||
    availableRoles.find((role) => role?.name === defaultRole)?.displayName ||
    'General';

  // Build tracking cache from questions/notes/pitches/advanced
  // (grouped by role within the selected playbook)
  const trackingCache: Record<string, SelectionTrackingData> = {};

  const entryForSelection = (playbookName: string, role: string | undefined): SelectionTrackingData => {
    const cacheKey = `${playbookName}|${role || defaultRole}`;
    if (!trackingCache[cacheKey]) {
      trackingCache[cacheKey] = createEmptyTrackingEntry();
    }
    return trackingCache[cacheKey];
  };

  // Current saver writes arrays inside rolesUsed; older sessions may have
  // top-level arrays. Keep the fallback without mixing two representations.
  const roleEntries = Array.isArray(salesPlaybookUsage?.rolesUsed) && salesPlaybookUsage.rolesUsed.length > 0
    ? salesPlaybookUsage.rolesUsed
    : [salesPlaybookUsage];

  for (const roleEntry of roleEntries) {
    const playbookName = roleEntry?.playbookName || selectedPlaybookName;
    const entryForItem = (item: any) => entryForSelection(playbookName, item?.role || roleEntry?.role);
    for (const asked of roleEntry?.questionsAsked || []) {
      const cacheEntry = entryForItem(asked);
      if (asked?.questionId && !cacheEntry.askedQuestions.includes(asked.questionId)) {
        cacheEntry.askedQuestions.push(asked.questionId);
      }
    }
    for (const questionNote of roleEntry?.questionNotes || []) {
      if (questionNote?.questionId) {
        const cacheEntry = entryForItem(questionNote);
        cacheEntry.questionNotes[questionNote.questionId] = {
          note: questionNote.note,
          timestamp: questionNote.timestamp,
        };
      }
    }
    for (const pitchUsed of roleEntry?.pitchesUsed || []) {
      if (pitchUsed?.pitchId) {
        const cacheEntry = entryForItem(pitchUsed);
        if (!cacheEntry.checkedPitches.includes(pitchUsed.pitchId)) cacheEntry.checkedPitches.push(pitchUsed.pitchId);
        if (pitchUsed.pitchText) cacheEntry.legacyPitchTexts![pitchUsed.pitchId] = pitchUsed.pitchText;
      }
    }
    for (const pitchNote of roleEntry?.pitchNotes || []) {
      if (pitchNote?.pitchId) {
        const cacheEntry = entryForItem(pitchNote);
        cacheEntry.pitchNotes[pitchNote.pitchId] = { note: pitchNote.note, timestamp: pitchNote.timestamp };
        if (pitchNote.pitchText) cacheEntry.legacyPitchTexts![pitchNote.pitchId] = pitchNote.pitchText;
      }
    }
    for (const advancedUsed of roleEntry?.advancedUsed || []) {
      if (advancedUsed?.advancedId) {
        const cacheEntry = entryForItem(advancedUsed);
        if (!cacheEntry.checkedAdvanced.includes(advancedUsed.advancedId)) cacheEntry.checkedAdvanced.push(advancedUsed.advancedId);
        if (advancedUsed.advancedText) cacheEntry.legacyAdvancedTexts![advancedUsed.advancedId] = advancedUsed.advancedText;
      }
    }
    for (const advancedNote of roleEntry?.advancedNotes || []) {
      if (advancedNote?.advancedId) {
        const cacheEntry = entryForItem(advancedNote);
        cacheEntry.advancedNotes[advancedNote.advancedId] = { note: advancedNote.note, timestamp: advancedNote.timestamp };
        if (advancedNote.advancedText) cacheEntry.legacyAdvancedTexts![advancedNote.advancedId] = advancedNote.advancedText;
      }
    }
  }

  // Get current selection's data from cache
  const currentKey = `${selectedPlaybookName}|${defaultRole}`;
  const currentTracking = trackingCache[currentKey] || createEmptyTrackingEntry();

  return {
    defaultRole,
    restorePayload: {
      selectedPlaybookName,
      selectedPlaybookDisplayName:
        salesPlaybookUsage?.playbookDisplayName || salesPlaybookUsage?.selectedPlaybookDisplayName || selectedPlaybookName,
      selectedRole: defaultRole,
      selectedRoleDisplayName: defaultRoleDisplayName,
      availableRoles,
      askedQuestions: currentTracking.askedQuestions,
      questionNotes: currentTracking.questionNotes,
      checkedPitches: currentTracking.checkedPitches,
      pitchNotes: currentTracking.pitchNotes,
      checkedAdvanced: currentTracking.checkedAdvanced,
      advancedNotes: currentTracking.advancedNotes,
      legacyPitchTexts: currentTracking.legacyPitchTexts,
      legacyAdvancedTexts: currentTracking.legacyAdvancedTexts,
      trackingCache,
    },
  };
}
